import { fetchAudioAsync, fetchAudioPreviewAsync } from 'actions/annotation-actions';
import React, { useEffect, useRef, useCallback, useState } from 'react';
import {connect} from 'react-redux';
import { CombinedState } from 'reducers';
import { getPlaybackFrameRate, getTimelineFrameRate } from 'utils/media-time';

const AUDIO_DURATION_MISMATCH_RATIO_GUARD = 0.2;
const AUDIO_SYNC_HARD_DRIFT_SECONDS_THRESHOLD = 0.45;
const AUDIO_SYNC_DIAGNOSTICS_INTERVAL_MS = 250;
const AUDIO_SYNC_RATE_CORRECTION_DEADBAND_SECONDS = 0.03;
const AUDIO_SYNC_RATE_CORRECTION_GAIN = 0.35;
const AUDIO_SYNC_RATE_CORRECTION_MAX_DELTA = 0.08;
const AUDIO_SYNC_MIN_HARD_SYNC_INTERVAL_MS = 750;
const AUDIO_PLAYBACK_RATE_EPSILON = 0.01;
const AUDIO_PLAYBACK_RATE_MIN = 0.25;
const AUDIO_PLAYBACK_RATE_MAX = 4;
const AUDIO_SOURCE_MIME = 'audio/mp4';

interface StateToProps {
    audioData: any,
    audioFetching: boolean;
    playing: boolean
    frameNumber: number;
    startFrame: number;
    stopFrame: number;
    frameSpeed: number;
    playbackReferenceFrame: number | null;
    playbackReferenceTimestamp: number | null;
    jobPhase: string;
    dataFrameRate: number | null;
}

interface DispatchToProps {
    fetchAudio: () => void;
    fetchPreview: () => void;
}


function mapStateToProps(state: CombinedState): StateToProps {
    const {
        annotation: {
            job: {
                instance: {
                    startFrame,
                    stopFrame,
                    phase: jobPhase,
                    dataFrameRate,
                }
            },
            player: {
                audio: {
                    data: audioData,
                    fetching: audioFetching,

                },
                playback: {
                    referenceFrame: playbackReferenceFrame,
                    referenceTimestamp: playbackReferenceTimestamp,
                },
                playing,
                frame: {
                    number: frameNumber,
                },
            },
        },
        settings: {
            player: {
                frameSpeed
            }
        }
    } = state;
    return {
        audioData,
        audioFetching,
        playing,
        frameNumber,
        startFrame,
        stopFrame,
        frameSpeed,
        playbackReferenceFrame,
        playbackReferenceTimestamp,
        jobPhase,
        dataFrameRate,
    }
}

function mapDispatchToProps(dispatch: any): DispatchToProps {
    return {
        fetchAudio: (): void => dispatch(fetchAudioAsync()),
        fetchPreview: (): void => dispatch(fetchAudioPreviewAsync()),
    }
}

function AudioPlaybackComponent(props: StateToProps & DispatchToProps) {
    const {
        audioData,
        audioFetching,
        playing,
        frameNumber,
        frameSpeed,
        startFrame,
        stopFrame,
        playbackReferenceFrame,
        playbackReferenceTimestamp,
        fetchAudio,
        fetchPreview,
        jobPhase,
        dataFrameRate,
    } = props;

    const [syncDiagnostics, setSyncDiagnostics] = useState({
        mismatchDetected: false,
        useAudioRatioTimeline: false,
        driftSeconds: 0,
        segmentDurationSeconds: 0,
        audioDurationSeconds: 0,
    });
    const [audioReady, setAudioReady] = useState(false);
    const audioRef = useRef<HTMLAudioElement>(null);
    const audioObjectURLRef = useRef<string | null>(null);
    const syncLoopFrameRef = useRef<number | null>(null);
    const lastDiagnosticsTimestampRef = useRef(0);
    const lastHardSyncTimestampRef = useRef(0);

    useEffect(() => {
        if (!audioData && !audioFetching) {
            fetchAudio();
            fetchPreview();
        }
    }, [audioData, audioFetching, fetchAudio, fetchPreview]);

    const stopSyncLoop = useCallback(() => {
        if (syncLoopFrameRef.current !== null) {
            window.cancelAnimationFrame(syncLoopFrameRef.current);
            syncLoopFrameRef.current = null;
        }
    }, []);

    const getSyncConfig = useCallback((audioElement: HTMLAudioElement | null) => {
        if (!audioElement || !audioElement.duration || !frameSpeed) {
            return null;
        }

        const audioDurationSeconds = audioElement.duration;
        const totalFrames = Math.max(1, stopFrame - startFrame + 1);
        const timelineFrameRate = getTimelineFrameRate(dataFrameRate);
        const playbackFrameRate = getPlaybackFrameRate(dataFrameRate, frameSpeed);
        const mediaFrameRate = totalFrames / audioDurationSeconds;
        const segmentDurationSeconds = totalFrames / timelineFrameRate;
        const mismatchDetected = segmentDurationSeconds > 0 &&
            Math.abs(audioDurationSeconds - segmentDurationSeconds) / segmentDurationSeconds > AUDIO_DURATION_MISMATCH_RATIO_GUARD;
        const desiredPlaybackRate = Math.max(
            AUDIO_PLAYBACK_RATE_MIN,
            Math.min(AUDIO_PLAYBACK_RATE_MAX, playbackFrameRate / mediaFrameRate),
        );

        return {
            audioDurationSeconds,
            desiredPlaybackRate,
            mediaFrameRate,
            mismatchDetected,
            segmentDurationSeconds,
            totalFrames,
        };
    }, [dataFrameRate, frameSpeed, startFrame, stopFrame]);

    const getExpectedAudioTimeForFrame = useCallback((frame: number, totalFrames: number, mediaFrameRate: number, audioDurationSeconds: number) => {
        const maxRelativeFrame = totalFrames - 1;
        const relativeFrame = Math.max(0, Math.min(frame - startFrame, maxRelativeFrame));
        return Math.max(0, Math.min(relativeFrame / mediaFrameRate, audioDurationSeconds));
    }, [startFrame]);

    const updateDiagnostics = useCallback((
        mismatchDetected: boolean,
        driftSeconds: number,
        segmentDurationSeconds: number,
        audioDurationSeconds: number,
    ) => {
        setSyncDiagnostics({
            mismatchDetected,
            useAudioRatioTimeline: true,
            driftSeconds,
            segmentDurationSeconds,
            audioDurationSeconds,
        });
    }, []);

    const getCorrectedPlaybackRate = useCallback((desiredPlaybackRate: number, driftSeconds: number) => {
        if (Math.abs(driftSeconds) <= AUDIO_SYNC_RATE_CORRECTION_DEADBAND_SECONDS) {
            return desiredPlaybackRate;
        }

        const correctionDelta = Math.max(
            -AUDIO_SYNC_RATE_CORRECTION_MAX_DELTA,
            Math.min(AUDIO_SYNC_RATE_CORRECTION_MAX_DELTA, -driftSeconds * AUDIO_SYNC_RATE_CORRECTION_GAIN),
        );

        return Math.max(
            AUDIO_PLAYBACK_RATE_MIN,
            Math.min(AUDIO_PLAYBACK_RATE_MAX, desiredPlaybackRate + correctionDelta),
        );
    }, []);

    useEffect(() => {
        if (!audioFetching && audioData) {
            if (audioRef.current) {
                if (audioObjectURLRef.current) {
                    URL.revokeObjectURL(audioObjectURLRef.current);
                }
                const objectURL = URL.createObjectURL(new Blob([audioData], { type: AUDIO_SOURCE_MIME }));
                audioObjectURLRef.current = objectURL;
                audioRef.current.src = objectURL;
                setAudioReady(false);
            }
        }
    }, [audioData, audioFetching]);

    useEffect(() => () => {
        stopSyncLoop();
        if (audioRef.current?.src) {
            audioRef.current.pause();
        }
        if (audioObjectURLRef.current) {
            URL.revokeObjectURL(audioObjectURLRef.current);
        }
    }, [stopSyncLoop]);

    useEffect(() => {
        if (
            !audioReady ||
            !audioRef.current ||
            playing ||
            !frameSpeed
        ) {
            return;
        }
        stopSyncLoop();
        const syncConfig = getSyncConfig(audioRef.current);
        if (!syncConfig) {
            return;
        }

        if (!audioRef.current.paused) {
            audioRef.current.pause();
        }

        if (Math.abs(audioRef.current.playbackRate - syncConfig.desiredPlaybackRate) > AUDIO_PLAYBACK_RATE_EPSILON) {
            audioRef.current.playbackRate = syncConfig.desiredPlaybackRate;
        }

        const expectedAudioTime = getExpectedAudioTimeForFrame(
            frameNumber,
            syncConfig.totalFrames,
            syncConfig.mediaFrameRate,
            syncConfig.audioDurationSeconds,
        );
        const driftSeconds = audioRef.current.currentTime - expectedAudioTime;
        if (Math.abs(driftSeconds) > AUDIO_PLAYBACK_RATE_EPSILON) {
            audioRef.current.currentTime = expectedAudioTime;
        }
        updateDiagnostics(
            syncConfig.mismatchDetected,
            driftSeconds,
            syncConfig.segmentDurationSeconds,
            syncConfig.audioDurationSeconds,
        );
        lastDiagnosticsTimestampRef.current = performance.now();
        lastHardSyncTimestampRef.current = lastDiagnosticsTimestampRef.current;
    }, [audioReady, frameNumber, frameSpeed, getExpectedAudioTimeForFrame, getSyncConfig, playing, stopSyncLoop, updateDiagnostics]);

    useEffect(() => {
        if (
            !playing ||
            !audioReady ||
            playbackReferenceFrame === null ||
            playbackReferenceTimestamp === null
        ) {
            stopSyncLoop();
            if (audioRef.current && !audioRef.current.paused) {
                audioRef.current.pause();
            }
            return undefined;
        }

        const syncTick = (): void => {
            if (!audioRef.current) {
                syncLoopFrameRef.current = window.requestAnimationFrame(syncTick);
                return;
            }

            const syncConfig = getSyncConfig(audioRef.current);
            if (!syncConfig) {
                syncLoopFrameRef.current = window.requestAnimationFrame(syncTick);
                return;
            }

            const elapsedSeconds = Math.max(0, (performance.now() - playbackReferenceTimestamp) / 1000);
            const expectedFrame = playbackReferenceFrame + elapsedSeconds * getPlaybackFrameRate(dataFrameRate, frameSpeed);
            const expectedAudioTime = getExpectedAudioTimeForFrame(
                expectedFrame,
                syncConfig.totalFrames,
                syncConfig.mediaFrameRate,
                syncConfig.audioDurationSeconds,
            );
            const driftSeconds = audioRef.current.currentTime - expectedAudioTime;
            const now = performance.now();
            const driftMagnitude = Math.abs(driftSeconds);
            const correctedPlaybackRate = getCorrectedPlaybackRate(syncConfig.desiredPlaybackRate, driftSeconds);
            const shouldHardSync = driftMagnitude > AUDIO_SYNC_HARD_DRIFT_SECONDS_THRESHOLD &&
                now - lastHardSyncTimestampRef.current >= AUDIO_SYNC_MIN_HARD_SYNC_INTERVAL_MS;

            if (audioRef.current.paused) {
                if (driftMagnitude > AUDIO_PLAYBACK_RATE_EPSILON) {
                    audioRef.current.currentTime = expectedAudioTime;
                    lastHardSyncTimestampRef.current = now;
                }
                audioRef.current.playbackRate = correctedPlaybackRate;
                void audioRef.current.play().catch(() => undefined);
            } else if (shouldHardSync) {
                audioRef.current.currentTime = expectedAudioTime;
                lastHardSyncTimestampRef.current = now;
                if (Math.abs(audioRef.current.playbackRate - syncConfig.desiredPlaybackRate) > AUDIO_PLAYBACK_RATE_EPSILON) {
                    audioRef.current.playbackRate = syncConfig.desiredPlaybackRate;
                }
            } else if (Math.abs(audioRef.current.playbackRate - correctedPlaybackRate) > AUDIO_PLAYBACK_RATE_EPSILON) {
                audioRef.current.playbackRate = correctedPlaybackRate;
            }

            if (now - lastDiagnosticsTimestampRef.current >= AUDIO_SYNC_DIAGNOSTICS_INTERVAL_MS) {
                updateDiagnostics(
                    syncConfig.mismatchDetected,
                    driftSeconds,
                    syncConfig.segmentDurationSeconds,
                    syncConfig.audioDurationSeconds,
                );
                lastDiagnosticsTimestampRef.current = now;
            }

            syncLoopFrameRef.current = window.requestAnimationFrame(syncTick);
        };

        stopSyncLoop();
        syncTick();

        return stopSyncLoop;
    }, [
        audioReady,
        dataFrameRate,
        frameSpeed,
        getExpectedAudioTimeForFrame,
        getSyncConfig,
        playbackReferenceFrame,
        playbackReferenceTimestamp,
        playing,
        stopSyncLoop,
        updateDiagnostics,
    ]);

    return <>
        <audio
            ref={audioRef}
            onEmptied={() => setAudioReady(false)}
            onLoadedMetadata={() => setAudioReady(true)}
            style={{display: 'none'}}
        />
        {['phase1a', 'phase1b'].includes(jobPhase) && syncDiagnostics.audioDurationSeconds > 0 && syncDiagnostics.mismatchDetected && (
            <div
                style={{
                    position: 'fixed',
                    right: 16,
                    bottom: 16,
                    zIndex: 1200,
                    background: 'rgba(173, 124, 0, 0.92)',
                    color: '#fff',
                    padding: '8px 10px',
                    borderRadius: 6,
                    fontSize: 12,
                    lineHeight: 1.4,
                    maxWidth: 360,
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
                    pointerEvents: 'none',
                }}
            >
                <div>
                    Playback Sync Guard: Mismatch Detected
                </div>
                <div>
                    Timeline: {syncDiagnostics.useAudioRatioTimeline ? 'Frame progress ratio' : 'Job segment time'}
                </div>
                <div>
                    Drift: {(syncDiagnostics.driftSeconds * 1000).toFixed(0)}ms
                </div>
                <div>
                    Segment: {syncDiagnostics.segmentDurationSeconds.toFixed(1)}s | Audio: {syncDiagnostics.audioDurationSeconds.toFixed(1)}s
                </div>
            </div>
        )}
    </>
}

export default connect(mapStateToProps, mapDispatchToProps)(AudioPlaybackComponent);
